<template>
    <search-form-layout :popup="popup" title="cruises.cruises" @close-popup="$emit('close-popup')">
        <template v-slot:form>
            <v-form ref="form" v-model="valid" class="cruise-form">
                <v-row>
                    <v-col cols="12" md="10">
                        <v-row>
                            <v-col md="4" xl="3" cols="12">
                                <city-country-autocomplete
                                    v-if="$conf.public.countrySearchCruises"
                                    v-model="departureCityCountryModel"
                                    label="cruises.departure_location"
                                    single-line
                                    filled
                                    product-type="CRUISE"
                                    location-type="DEPARTURE"
                                    :default-cities="popularDepartures"
                                />
                                <city-autocomplete
                                    v-else
                                    v-model="departureCity"
                                    label="cruises.departure_location"
                                    single-line
                                    filled
                                    product-type="CRUISE"
                                    location-type="DEPARTURE"
                                    :default-cities="popularDepartures"
                                />
                            </v-col>
                            <v-col md="4" xl="3" cols="12">
                                <city-country-autocomplete
                                    v-if="$conf.public.countrySearchCruises"
                                    v-model="destinationCityCountryModel"
                                    label="cruises.destination_location"
                                    single-line
                                    filled
                                    product-type="CRUISE"
                                    :rules="[
                                        v =>
                                            !!v || `${$t('cruises.destination_location')} ${$t('validation.required')}`,
                                    ]"
                                    location-type="DESTINATION"
                                    :default-cities="popularDestinations"
                                />
                                <city-autocomplete
                                    v-else
                                    v-model="destinationCity"
                                    label="cruises.destination_location"
                                    single-line
                                    filled
                                    product-type="CRUISE"
                                    :rules="[
                                        v =>
                                            !!v || `${$t('cruises.destination_location')} ${$t('validation.required')}`,
                                    ]"
                                    location-type="DESTINATION"
                                    :default-cities="popularDestinations"
                                />
                            </v-col>
                            <v-col md="4" cols="12">
                                <daterange-m
                                    label=""
                                    product="cruises"
                                    :_start="startDate"
                                    :_end="endDate"
                                    :max-nights="30"
                                    @change-start="startDate = $event"
                                    @change-end="endDate = $event"
                                />
                            </v-col>
                            <v-col md="4" xl="2" cols="12">
                                <tourists-select-menu
                                    :_adults="adults"
                                    :_children-ages="childrenAges"
                                    :max-total="4"
                                    token="guest"
                                    @change-adults="adults = $event"
                                    @change-children-ages="childrenAges = $event"
                                />
                            </v-col>
                            <v-col md="2" cols="12">
                                <v-text-field
                                    v-model="durationFrom"
                                    :placeholder="$t('cruises.duration_from')"
                                    single-line
                                    type="number"
                                    bg-color="white"
                                    hide-details
                                    :min="0"
                                    :class="{caption: $breakpoint.smAndDown}"
                                />
                            </v-col>
                            <v-col md="2" cols="12">
                                <v-text-field
                                    v-model="durationTo"
                                    :placeholder="$t('cruises.duration_to')"
                                    single-line
                                    type="number"
                                    bg-color="white"
                                    hide-details
                                    :min="0"
                                    :class="{caption: $breakpoint.smAndDown}"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col md="2" cols="12" class="hidden-sm-and-down">
                        <v-btn block size="x-large" color="primary" :disabled="!valid" @click="search">
                            {{ $t('search') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-form>
            <additional-options-wrapper
                ref="addForm"
                :suppliers="suppliers"
                :options="['internalSuppliers', 'externalSuppliers', 'supplierId']"
            />
            <v-row class="hidden-md-and-up">
                <v-col md="2" cols="12">
                    <v-btn block size="x-large" color="primary" :disabled="!valid" @click="search">
                        {{ $t('search') }}
                    </v-btn>
                </v-col>
            </v-row>
        </template>
    </search-form-layout>
</template>

<script lang="ts">
    import {Component, Prop} from 'vue-facing-decorator'
    import SearchFormLayout from '@/components/parts/SearchFormLayout.vue'
    import CityAutocomplete from '@/components/search/forms/CityAutocomplete.vue'
    import TouristsSelectMenu from '~src/components/search/forms/touristsSelectMenu.src'
    import {AdditionalSearchOptionsMixin} from '@/mixins/AdditionalSearchOptionsMixin.vue'
    import AdditionalOptionsWrapper from '~src/components/snippets/AdditionalSearchOptions/additionalOptionsWrapper.src'
    import DaterangeM from '~src/components/search/forms/daterangeM.src'
    import {CHANGE_TAB_MOBILE_EVENT, EventBus} from '@/utils/event-bus'
    import CityCountryAutocomplete from '~/components/search/forms/CityCountryAutocomplete.vue'

    // TODO Needs refactoring with other forms
    @Component({
        components: {
            CityCountryAutocomplete,
            DaterangeM,
            TouristsSelectMenu,
            SearchFormLayout,
            CityAutocomplete,
            AdditionalOptionsWrapper,
        },
    })
    export class CruisesSearchForm extends AdditionalSearchOptionsMixin {
        @Prop({default: false}) popup

        valid = false
        departureCity = null
        destinationCity = null

        departureCountry = null
        destinationCountry = null

        adults = 1
        childrenAges = []
        startDate = null
        endDate = null
        durationFrom = null
        durationTo = null
        suppliers = []
        popularDepartures = []
        popularDestinations = []

        async mounted() {
            await this.$store.restored
            await this.load()
        }

        async load() {
            await this.loadPopularLocations()

            if (this.departureCity?.id || this.searchRequest.departureCityId) {
                await this.loadDepartureCity()
            } else if (this.searchRequest.departureCountryId) {
                await this.loadDepartureCountry()
            }

            if (this.destinationCity?.id || this.searchRequest.destinationCityId) {
                await this.loadDestinationCity()
            } else if (this.searchRequest.destinationCountryId) {
                await this.loadDestinationCountry()
            }

            this.startDate = this.searchRequest.startDate
            this.endDate = this.searchRequest.endDate
            this.durationFrom = this.searchRequest.durationFrom
            this.durationTo = this.searchRequest.durationTo
            this.adults = authStore.user.isEmployeeOrExternalCorp ? 1 : this.searchRequest.adults
            this.childrenAges = authStore.user.isEmployeeOrExternalCorp ? [] : [...this.searchRequest.childrenAges]
            if (this.isTO1) {
                try {
                    this.suppliers = (await this.$api.suppliers.get({productType: 'cruises'}))?.suppliers
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            }
        }

        async loadDepartureCity() {
            const departureCityId = this.departureCity?.id || this.searchRequest.departureCityId
            if (departureCityId && cruisesRuntimeStore.departureCity.id !== departureCityId) {
                await cruisesRuntimeStore.loadDepartureCity(departureCityId)
            }
            this.departureCity =
                this.searchRequest.departureCityId && Object.keys(cruisesRuntimeStore.departureCity).length
                    ? cruisesRuntimeStore.departureCity
                    : null
        }

        async loadDestinationCity() {
            const destinationCityId = this.destinationCity?.id || this.searchRequest.destinationCityId
            if (destinationCityId && cruisesRuntimeStore.destinationCity.id !== destinationCityId) {
                await cruisesRuntimeStore.loadDestinationCity(destinationCityId)
            }
            this.destinationCity =
                destinationCityId && Object.keys(cruisesRuntimeStore.destinationCity).length
                    ? cruisesRuntimeStore.destinationCity
                    : null
        }

        async loadDepartureCountry() {
            await cruisesRuntimeStore.loadDepartureCountry(this.searchRequest.departureCountryId)
            this.departureCountry =
                this.searchRequest.departureCountryId && Object.keys(cruisesRuntimeStore.departureCountry).length
                    ? cruisesRuntimeStore.departureCountry
                    : null
        }

        async loadDestinationCountry() {
            await cruisesRuntimeStore.loadDestinationCountry(this.searchRequest.destinationCountryId)
            this.destinationCountry =
                this.searchRequest.destinationCountryId && Object.keys(cruisesRuntimeStore.destinationCountry).length
                    ? cruisesRuntimeStore.destinationCountry
                    : null
        }

        newQuery() {
            const query = {
                startDate: this.startDate,
                endDate: this.endDate,
                adults: this.adults,
                childrenAges: this.childrenAges,
                convertToCurrency: persistentStore.currency,
                ...(this.departureCity?.id && {departureCityId: this.departureCity.id}),
                ...(this.departureCountry?.id &&
                    !this.departureCity?.id && {departureCountryId: this.departureCountry.id}),
                ...(this.destinationCity?.id && {destinationCityId: this.destinationCity.id}),
                ...(this.destinationCountry?.id &&
                    !this.destinationCity?.id && {destinationCountryId: this.destinationCountry.id}),
                ...(this.durationFrom && {durationFrom: this.durationFrom}),
                ...(this.durationTo && {durationTo: this.durationTo}),
            }

            if (persistentStore.selectedCompanyAndAgent) {
                query.clientId = persistentStore.selectedCompanyAndAgent.company.id

                if (persistentStore.selectedCompanyAndAgent.agent) {
                    query.agentId = persistentStore.selectedCompanyAndAgent.agent.personId
                }
            }

            return query
        }

        async search() {
            this.$emit('close-popup')

            if (this.departureCity?.id) {
                cruisesRuntimeStore.SET_DEPARTURE_CITY(this.departureCity)
            } else if (this.departureCountry?.id) {
                cruisesRuntimeStore.SET_DEPARTURE_COUNTRY(this.departureCountry)
            } else {
                cruisesRuntimeStore.SET_DEPARTURE_COUNTRY({})
            }

            if (this.destinationCity?.id) {
                cruisesRuntimeStore.SET_DESTINATION_CITY(this.destinationCity)
            } else if (this.destinationCountry?.id) {
                cruisesRuntimeStore.SET_DESTINATION_COUNTRY(this.destinationCountry)
            } else {
                cruisesRuntimeStore.SET_DESTINATION_COUNTRY({})
            }

            const query = this.newQuery()
            try {
                await this.$router.push({name: 'cruises', query})

                // eslint-disable-next-line no-empty
            } catch (e) {}
            EventBus.$emit(CHANGE_TAB_MOBILE_EVENT, 1)
        }

        async loadPopularLocations() {
            await Promise.allSettled([
                (async () => {
                    this.popularDepartures = (
                        await this.$api.locations.get({
                            limitCities: 10,
                            productType: 'CRUISE',
                            locationType: 'DEPARTURE',
                        })
                    ).cities
                })(),
                (async () => {
                    this.popularDestinations = (
                        await this.$api.locations.get({
                            limitCities: 10,
                            productType: 'CRUISE',
                            locationType: 'DESTINATION',
                        })
                    ).cities
                })(),
            ])
        }

        get isTO1() {
            return authStore.isTO1
        }

        get minDateTime() {
            return this.$dateFns.format(new Date())
        }

        get searchRequest() {
            return cruisesStore.searchRequest
        }

        get departureCityCountryModel() {
            if (this.departureCountry) {
                return this.departureCountry
            }

            return this.departureCity
        }

        set departureCityCountryModel(location) {
            if (!location) {
                this.departureCity = null
                this.departureCountry = null
            }

            if (location?.type === 'country') {
                this.departureCity = null
                this.departureCountry = location
            } else {
                this.departureCountry = null
                this.departureCity = location
            }
        }

        get destinationCityCountryModel() {
            if (this.destinationCountry) {
                return this.destinationCountry
            }

            return this.destinationCity
        }

        set destinationCityCountryModel(location) {
            if (!location) {
                this.destinationCity = null
                this.destinationCountry = null
            }

            if (location?.type === 'country') {
                this.destinationCity = null
                this.destinationCountry = location
            } else {
                this.destinationCountry = null
                this.destinationCity = location
            }
        }
    }

    export default CruisesSearchForm
</script>

<style lang="scss" scoped>
    :deep(.v-dialog) {
        overflow-y: hidden;
    }
    :deep(.v-label) {
        font-size: inherit;
    }
</style>
